import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ArrowRight, Check, X, FileText, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const consentItems = [
  'I understand that HealthLink360 provides wellness guidance, not medical advice.',
  'I consent to the collection and processing of my health data for personalized insights.',
  'I understand my data is encrypted and protected following HIPAA-adjacent standards.',
  'I can request deletion of my data at any time through Settings.',
];

const privacyPolicySummary = {
  title: 'Privacy Policy Summary',
  lastUpdated: 'January 2026',
  sections: [
    {
      title: 'What We Collect',
      content: 'We collect biometric data (heart rate, HRV, sleep patterns), mood entries, and profile information to personalize your wellness experience.'
    },
    {
      title: 'How We Use It',
      content: 'Your data is used to calculate your Digital Twin Score, provide AI coaching recommendations, and track your wellness progress. We never sell your data.'
    },
    {
      title: 'Data Security',
      content: 'All data is encrypted in transit and at rest using industry-standard AES-256 encryption. We follow HIPAA-adjacent security practices.'
    },
    {
      title: 'Your Rights',
      content: 'You can export, delete, or request a copy of your data at any time through Settings. Data deletion requests are processed within 30 days.'
    },
    {
      title: 'Third Parties',
      content: 'We only share anonymized, de-identified, aggregated data for research purposes. Your personal information is never shared with advertisers or sold to third parties.'
    }
  ]
};

export default function ConsentPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [checked, setChecked] = useState(consentItems.map(() => false));
  const [loading, setLoading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const allChecked = checked.every(Boolean);

  const handleToggle = (index) => {
    setChecked(prev => {
      const newChecked = [...prev];
      newChecked[index] = !newChecked[index];
      return newChecked;
    });
  };

  const handleContinue = async () => {
    if (!allChecked) {
      toast.error('Please agree to all terms to continue');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/user/consent`, { consent_given: true });
      updateUser({ consent_given: true });
      toast.success('Thank you for your consent');
      navigate('/onboarding');
    } catch (error) {
      toast.error('Failed to save consent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-2xl bg-primary/10">
              <ShieldCheck size={48} className="text-primary" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-heading font-bold mb-2">Your Privacy Matters</h1>
            <p className="text-muted-foreground text-sm">
              Before we begin, please review and agree to our data practices.
            </p>
          </div>

          {/* Consent Items */}
          <Card className="glass-card p-6 mb-4">
            <div className="space-y-4">
              {consentItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <Checkbox
                    id={`consent-${index}`}
                    checked={checked[index]}
                    onCheckedChange={() => handleToggle(index)}
                    className="mt-0.5"
                    data-testid={`consent-checkbox-${index}`}
                  />
                  <label
                    htmlFor={`consent-${index}`}
                    className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
                  >
                    {item}
                  </label>
                </motion.div>
              ))}
            </div>
          </Card>

          {/* Privacy Policy Link */}
          <button
            onClick={() => setShowPrivacyModal(true)}
            className="flex items-center justify-center gap-2 w-full text-sm text-primary hover:underline mb-6"
            data-testid="privacy-policy-link"
          >
            <FileText size={16} />
            Read our Privacy Policy Summary
            <ExternalLink size={14} />
          </button>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={!allChecked || loading}
            className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
            data-testid="consent-continue-btn"
          >
            {loading ? 'Saving...' : (
              <>
                I Agree & Continue
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>
        </motion.div>
      </main>

      {/* Progress Indicator */}
      <div className="flex justify-center gap-2 pb-8">
        <div className="w-8 h-1 rounded-full bg-primary" />
        <div className="w-8 h-1 rounded-full bg-muted" />
        <div className="w-8 h-1 rounded-full bg-muted" />
        <div className="w-8 h-1 rounded-full bg-muted" />
      </div>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowPrivacyModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg max-h-[80vh] overflow-hidden"
            >
              <Card className="glass-card overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-primary/10">
                      <FileText size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold">{privacyPolicySummary.title}</h2>
                      <p className="text-xs text-muted-foreground">Last updated: {privacyPolicySummary.lastUpdated}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPrivacyModal(false)}
                    className="rounded-full"
                    data-testid="close-privacy-modal"
                  >
                    <X size={20} />
                  </Button>
                </div>

                {/* Modal Content */}
                <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
                  {privacyPolicySummary.sections.map((section, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <h3 className="font-semibold text-sm mb-1">{section.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t border-border">
                  <Button
                    onClick={() => setShowPrivacyModal(false)}
                    className="w-full bg-primary hover:bg-primary/90 rounded-full"
                    data-testid="close-privacy-btn"
                  >
                    I Understand
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
